import { useState, useEffect } from "react";
import { Loader2, KeyRound, Check, Shield } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  OrganizationRole,
  roleLabels,
  roleDescriptions,
  submitOrganizationRoleRequest,
} from "@tickethall/core";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { panelControl, panelFieldLabel, panelPill, panelDialog } from "@/lib/panel-ui";

interface RequestRoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  organizationName?: string;
  currentRole?: OrganizationRole;
  initialRole?: OrganizationRole;
}

const SELECTABLE_ROLES: OrganizationRole[] = ["admin", "finance", "manager", "editor"];

export function RequestRoleModal({
  open,
  onOpenChange,
  organizationId,
  organizationName = "Organização",
  currentRole,
  initialRole = "finance",
}: RequestRoleModalProps) {
  const [selectedRole, setSelectedRole] = useState<OrganizationRole>(initialRole);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedRole(initialRole);
      setReason("");
    }
  }, [open, initialRole]);

  const handleSubmit = async () => {
    if (!organizationId || !selectedRole) return;
    setIsSubmitting(true);
    try {
      const res = await submitOrganizationRoleRequest(organizationId, selectedRole, reason);
      if (!res.success) {
        toast({
          title: "Não foi possível enviar a solicitação",
          description: res.error || "Ocorreu um erro ao processar seu pedido.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Solicitação enviada!",
          description: `Os administradores de ${organizationName} receberam seu pedido para o cargo de ${roleLabels[selectedRole]}.`,
        });
        onOpenChange(false);
      }
    } catch (err: any) {
      toast({
        title: "Erro ao enviar solicitação",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(panelDialog, "sm:max-w-md")}>
        <DialogHeader className="space-y-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary border border-primary/20">
            <KeyRound className="h-5 w-5" />
          </div>
          <div>
            <DialogTitle className="font-display text-lg font-semibold tracking-tight text-foreground">
              Solicitar Permissão de Acesso
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Envie um pedido aos administradores de <strong>{organizationName}</strong> para alterar seu cargo de acesso.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {currentRole && (
            <div className="flex items-center justify-between rounded-xl bg-secondary/40 border border-border/50 px-3.5 py-2.5">
              <span className="text-xs text-muted-foreground">Seu cargo atual:</span>
              <Badge variant="outline" className="font-medium text-xs rounded-full">
                {roleLabels[currentRole] || currentRole}
              </Badge>
            </div>
          )}

          <div>
            <label className={panelFieldLabel}>Cargo desejado</label>
            <div className="space-y-2 mt-1.5">
              {SELECTABLE_ROLES.map((role) => {
                const isSelected = selectedRole === role;
                const isCurrent = currentRole === role;
                return (
                  <button
                    key={role}
                    type="button"
                    disabled={isCurrent}
                    onClick={() => setSelectedRole(role)}
                    className={cn(
                      "w-full text-left rounded-2xl border p-3 transition-all flex items-start gap-3",
                      isSelected
                        ? "border-primary/40 bg-primary/5 text-foreground shadow-xs"
                        : "border-border/60 bg-secondary/20 hover:border-border hover:bg-secondary/40 text-muted-foreground",
                      isCurrent && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <div
                      className={cn(
                        "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-all",
                        isSelected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-muted-foreground/40 bg-background"
                      )}
                    >
                      {isSelected && <Check className="h-2.5 w-2.5 stroke-[3]" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-foreground">
                          {roleLabels[role]}
                        </span>
                        {isCurrent && (
                          <span className="text-[10px] text-muted-foreground">(Atual)</span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                        {roleDescriptions[role]}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className={panelFieldLabel} htmlFor="request-role-reason">
              Justificativa para os administradores
            </label>
            <Textarea
              id="request-role-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Preciso de acesso para realizar estornos dos participantes do evento ou gerenciar saques."
              rows={3}
              className={cn(panelControl, "mt-1.5 text-xs resize-none")}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2">
          <Button
            type="button"
            variant="ghost"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
            className={cn(panelPill, "text-xs h-9")}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={isSubmitting || selectedRole === currentRole}
            onClick={handleSubmit}
            className={cn(panelPill, "text-xs h-9 font-semibold gap-1.5")}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Enviando...
              </>
            ) : (
              "Enviar solicitação"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
